import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ToastVariant } from './useToast';

const variants = {
  success: 'border-emerald-200 bg-emerald-50 text-emerald-900',
  error: 'border-red-200 bg-red-50 text-red-900',
  info: 'border-blue-200 bg-blue-50 text-blue-900',
};
const accents = { success: 'bg-emerald-500', error: 'bg-red-500', info: 'bg-blue-500' };

/** Dismissal is presentation only: persistent operation owners keep their records. */
export function ToastSurface({ variant = 'info', children, durationMs, onDismiss }: {
  variant?: ToastVariant; children: React.ReactNode; durationMs?: number; onDismiss?: () => void;
}) {
  const [exit, setExit] = useState<'fade' | 'swipe' | 'closed' | null>(null);
  const [showClose, setShowClose] = useState(false);
  const suppressClick = useRef(false);
  const gesture = useRef<{ x: number; y: number; pointer: number } | null>(null);
  const timeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const callback = useRef(onDismiss); callback.current = onDismiss;
  const finish = () => { setExit('closed'); callback.current?.(); };
  const dismiss = (kind: 'fade' | 'swipe') => { setExit(kind); };
  useEffect(() => {
    const timer = setTimeout(() => dismiss('fade'), durationMs ?? (variant === 'error' ? 8000 : 5000));
    return () => clearTimeout(timer);
  }, [durationMs, variant]);
  useEffect(() => {
    if (exit !== 'fade' && exit !== 'swipe') return;
    timeout.current = setTimeout(finish, 220);
    return () => clearTimeout(timeout.current);
  }, [exit]);
  if (exit === 'closed') return null;
  return <div
    className={`group pointer-events-auto relative w-full max-w-sm overflow-hidden rounded-xl border shadow-lg backdrop-blur ${variants[variant]}`}
    style={{ touchAction: 'pan-y', transition: 'opacity 220ms ease, transform 220ms ease', opacity: exit ? 0 : 1, transform: exit === 'swipe' ? 'translateX(calc(100% + 2rem))' : undefined }}
    onClickCapture={event=>{if(suppressClick.current){event.preventDefault();event.stopPropagation();suppressClick.current=false;}}}
    onPointerDown={event => { suppressClick.current=false; if (event.pointerType !== 'mouse' && !(event.target as HTMLElement).closest('[data-toast-close]')) gesture.current = { x:event.clientX, y:event.clientY, pointer:event.pointerId }; }}
    onPointerCancel={() => { gesture.current = null; }}
    onPointerUp={event => {
      const start = gesture.current; gesture.current = null;
      if (!start || start.pointer !== event.pointerId) return;
      const dx = event.clientX - start.x, dy = event.clientY - start.y;
      if (dx > 48 && dx > Math.abs(dy) * 1.4) { suppressClick.current=true; dismiss('swipe'); }
      else if (Math.abs(dx) < 10 && Math.abs(dy) < 10) setShowClose(true);
    }}
  >
    <div className={`h-1 ${accents[variant]}`} />
    <button type="button" data-toast-close aria-label="Értesítés bezárása" onClick={finish}
      className={`absolute right-1 top-2 rounded-md px-2 py-1 text-lg leading-none focus:opacity-100 group-hover:opacity-100 group-focus-within:opacity-100 ${showClose ? 'opacity-100' : 'opacity-0'}`}>×</button>
    <div className="px-4 py-3 pr-9 text-sm">{children}</div>
  </div>;
}

/** A modal's backdrop must cover notices, including notices created after opening it. */
export function useToastLayer() {
  const [modal, setModal] = useState(()=>typeof document !== 'undefined' && typeof document.querySelector === 'function' && Boolean(document.querySelector('[aria-modal="true"], .modal-006-backdrop')));
  useEffect(() => {
    if (typeof document === 'undefined' || !document.body || typeof MutationObserver === 'undefined') return;
    const scan = () => setModal(Boolean(document.querySelector('[aria-modal="true"], .modal-006-backdrop')));
    scan();
    const observer = new MutationObserver(scan);
    observer.observe(document.body, { childList:true, subtree:true, attributes:true, attributeFilter:['aria-modal'] });
    return () => observer.disconnect();
  }, []);
  return modal ? 40 : 10000;
}
export function BottomToastRegion({ children, target }: { children: React.ReactNode; target?: React.RefObject<HTMLDivElement | null> }) {
  const zIndex = useToastLayer();
  if (target?.current?.nodeType === 1) return createPortal(<div className="contents">{children}</div>, target.current);
  const region = <div style={{ zIndex }} className="pointer-events-none fixed bottom-4 right-4 flex max-w-[calc(100vw-2rem)] flex-col items-end gap-2" aria-live="polite">{children}</div>;
  return typeof document !== 'undefined' && document.body?.nodeType === 1 ? createPortal(region, document.body) : region;
}
